import React from "react";

/**
 * Apple-required subscription disclosure block (Guideline 3.1.2(c)).
 *
 * Apple App Review requires apps offering auto-renewable subscriptions to
 * display, *inside the app itself*, all of the following:
 *
 *   - Title of each auto-renewing subscription
 *   - Length of subscription (time period + content/services provided)
 *   - Price of each subscription (and price per unit if appropriate)
 *   - Functional links to Terms of Use (EULA) + Privacy Policy
 *
 * Build 32 was rejected because the StoreKit price cards above pull title +
 * price asynchronously from RevenueCat. If offerings fail to load (slow
 * network in the App Review environment), the reviewer sees empty cards.
 * So we ALSO render the required info as plain hard-coded text here, which
 * always renders regardless of network state. Do not edit copy without
 * legal review.
 */
export default function PaywallIOSDisclosure({ onRestore = () => {} }) {
  return (
    <div
      data-testid="ios-subscription-disclosure"
      className="text-[12px] leading-snug text-navy font-jakarta text-left space-y-3 pt-1"
    >
      {/* Hard-coded subscription details — always renders so Apple Review
          can verify title / length / price even if StoreKit offerings are
          slow to load. Mirrors the dynamic price cards above. */}
      <div
        data-testid="ios-subscription-details"
        className="bg-bone rounded-2xl border border-navy/10 p-4 space-y-3"
      >
        <div className="font-jakarta font-extrabold text-navy text-sm uppercase tracking-wider">
          Subscription details
        </div>
        <div className="space-y-2.5 text-navy">
          <div className="flex justify-between gap-3" data-testid="ios-disclosure-monthly">
            <div>
              <div className="font-jakarta font-bold">
                TipLingo Pro Monthly
              </div>
              <div className="text-navy-soft text-[11px]">
                1 month · auto-renewable
              </div>
            </div>
            <div className="font-jakarta font-extrabold whitespace-nowrap">
              $4.99 / month
            </div>
          </div>
          <div className="flex justify-between gap-3" data-testid="ios-disclosure-yearly">
            <div>
              <div className="font-jakarta font-bold">
                TipLingo Pro Yearly
              </div>
              <div className="text-navy-soft text-[11px]">
                12 months · auto-renewable
              </div>
            </div>
            <div className="font-jakarta font-extrabold whitespace-nowrap">
              $39.99 / year
            </div>
          </div>
        </div>
        <div className="text-navy-soft text-[11px] leading-snug pt-1 border-t border-navy/10">
          Each subscription unlocks expanded daily AI translations
          (text + voice), expanded menu and receipt scans, a faster
          higher-quality AI model, priority AI queue, saved phrasebook
          access, offline language packs, and exclusive Pro features for
          the length of the subscription period.
        </div>
      </div>

      <p>
        Payment will be charged to your Apple ID account at confirmation of
        purchase. Subscriptions automatically renew at the same price unless
        auto-renew is turned off at least 24 hours before the end of the
        current period. Your account will be charged for renewal within 24
        hours prior to the end of the current period.
      </p>
      <p>
        You can manage your subscription and turn off auto-renew at any time
        in your Apple ID Settings → Subscriptions after purchase.
      </p>
      <p className="flex items-center justify-center gap-3 pt-2 text-[12px] font-jakarta font-bold flex-wrap text-navy-soft">
        {/* Footer trio — Apple-compliant: a functional Terms of Use link
            (our /terms IS our custom EULA so it satisfies the App Store
            Connect requirement; Apple’s Standard EULA fallback link is
            preserved on the in-app Settings → Terms surface) + Privacy
            Policy + Restore Purchases, mirroring the TipLingo V2 design. */}
        <a
          href="https://tiplingo.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="ios-terms-link"
          className="hover:text-navy underline"
        >
          Terms of Use
        </a>
        <span aria-hidden>·</span>
        <a
          href="https://tiplingo.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="ios-privacy-link"
          className="hover:text-navy underline"
        >
          Privacy Policy
        </a>
        <span aria-hidden>·</span>
        <button
          type="button"
          data-testid="ios-restore-link"
          onClick={onRestore}
          className="hover:text-navy underline font-jakarta font-bold"
        >
          Restore Purchases
        </button>
      </p>
    </div>
  );
}
